import { useGetSharedCalendar } from '@/api/useSharedCalendar';
import { useEffect, useMemo } from 'react';

type SharedCalendarConfigProps = {
  config?: {
    name?: string;
    label?: string;
    [key: string]: any;
  };
  isPlan?: boolean;
};

const DefaultCalendarOption = {
  label: "Organizer's calendar",
  value: 'organizer_calendar',
  calendarId: 'organizer_calendar',
  userId: 0,
  isPrimary: false,
  isDefault: false,
};

// note: only covered the interview plan flow here
export const useSharedCalendarConfig = ({
  config,
  isPlan = true,
}: SharedCalendarConfigProps = {}) => {
  const { name = 'calendar', ...overRides } = config ?? {};
  const { calendars, isLoading, fetchSharedCalendars } = useGetSharedCalendar();

  useEffect(() => {
    fetchSharedCalendars();
  }, [fetchSharedCalendars]);

  const calendarSelectOptions = useMemo(() => {
    const list = calendars ?? [];
    const filtered = isPlan
      ? list.filter((cal) => !cal?.calendarId?.includes?.('@kula.ai'))
      : list;
    const withDefault = isPlan
      ? [DefaultCalendarOption, ...filtered]
      : [...filtered];
    return withDefault;
  }, [calendars, isPlan]);

  // const isPrimaryCalendarOnlyAvailable = useMemo(
  //   () =>
  //     calendarSelectOptions.length === 1 &&
  //     calendarSelectOptions[0]?.isPrimary === true,
  //   [calendarSelectOptions]
  // );

  const calendarConfig = useMemo(() => {
    const base = {
      type: 'select' as const,
      label: 'Calendar',
      name,
      isLoading,
      options: calendarSelectOptions,
      ...overRides,
      customOnChange: (selectedOption: unknown, formik?: any) => {
        if (!isPlan) {
          formik?.setFieldValue?.('calendarUiViewMode', 'info');
        }
        overRides.customOnChange?.(selectedOption, formik);
      },
    };

    if (isPlan) {
      return base;
    }

    return {
      ...base,
      isConditional: true,
      conditionConfig: { field: 'calendarUiViewMode', value: 'calendar' },
    };
  }, [name, isLoading, calendarSelectOptions, overRides, isPlan]);

  const calendarInfo = useMemo(
    () => ({
      isConditional: true,
      conditionConfig: { field: 'calendarUiViewMode', value: 'info' },
      type: 'custom' as const,
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      Field: ({ formik }: { formik: any }) => (
        //todo: build and add calendar info here
        <></>
      ),
    }),
    []
  );

  const formConfig = useMemo(
    () => (isPlan ? [calendarConfig] : [calendarInfo, calendarConfig]),
    [isPlan, calendarInfo, calendarConfig]
  );

  return {
    formConfig,
    /** Spread into Formik initial values when using `isPlan: false`. */
    sharedCalendarInitialValues: isPlan ? {} : { calendarUiViewMode: 'info' },
  };
};
